import type { GitSidebarCategory, GitSidebarPage } from '@readme/api-contracts/schemas/sidebar';

interface BreadcrumbItem {
  slug?: string | null;
  title: string;
}

/*
 * Finds the breadcrumb trail for the current page based on the provided sidebar structure.
 * @returns An array of breadcrumb items leading to the current page.
 */
export default function getCurrentPageBreadcrumb(sidebar: GitSidebarCategory[], currentSlug: string): BreadcrumbItem[] {
  if (!sidebar || !currentSlug) return [];

  // Normalize the currentSlug by removing trailing slashes
  const normalizedCurrentSlug = currentSlug.replace(/\/+$/, '');

  // Recursive function to search through the sidebar hierarchy
  function searchInPages(pages: GitSidebarPage[], parentPath: BreadcrumbItem[] = []): BreadcrumbItem[] | null {
    const foundPage = pages.find(page => {
      return page.slug === normalizedCurrentSlug;
    });

    if (foundPage) {
      // Don't include the current page in the breadcrumb - just return the parent path
      return parentPath;
    }

    // Search in child pages
    const childResult = pages
      .map(page => {
        if (page.pages && page.pages.length > 0) {
          return searchInPages(page.pages, [...parentPath, { title: page.title, slug: page.slug }]);
        }
        return null;
      })
      .find(result => result !== null);

    return childResult || null;
  }

  // Search through all categories
  const categoryResult = sidebar
    .map(category => {
      const categoryPath = [{ title: category.title, slug: null }]; // Categories are not clickable

      if (category.pages && category.pages.length > 0) {
        return searchInPages(category.pages, categoryPath);
      }
      return null;
    })
    .find(result => result !== null);

  return categoryResult || [];
}
